import { useState, useEffect, useCallback, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { OverlayMode } from 'klinecharts';
import { Z_INDEX } from '@/constants/zIndex';
import type { Chart, OverlayCreate, OverlayEvent } from 'klinecharts';
import { OverlayStyleModal } from './OverlayStyleModal';
import { TextBoxModal } from './TextBoxModal';
import { TEXT_BOX_DEFAULT } from '@/overlays/textBox';
import { useChartStore } from '@/store/chartStore';
import { trackDrawingTool } from '@/services/gtag';
import {
  TrendLineIcon,
  HorizontalLineIcon,
  VerticalLineIcon,
  RayLineIcon,
  HorizontalRayLineIcon,
  VerticalRayLineIcon,
  SegmentLineIcon,
  HorizontalSegmentIcon,
  VerticalSegmentIcon,
  PriceLineIcon,
  PriceChannelIcon,
  ParallelLinesIcon,
  FibonacciIcon,
  AnnotationIcon,
  TagIcon,
  TextBoxIcon,
  MagnetIcon,
  LockIcon,
  UnlockIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
  ChevronRightIcon,
  ArrowIcon,
  RectIcon,
  CircleIcon,
  TriangleIcon,
  ParallelogramIcon,
  FibExtensionIcon,
  FibFanIcon,
  FibCircleIcon,
  FibSegmentIcon,
  FibSpiralIcon,
  GannBoxIcon,
  WaveIcon,
  AbcdIcon,
  CurveIcon,
} from '@/components/icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface DrawingTool {
  mark: string;
  label: string;
  icon: React.ReactNode;
}

interface DrawingGroup {
  key: string;
  tools: DrawingTool[];
}

interface FlyoutState {
  type: 'group' | 'magnet';
  key: string;
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Static data â€” built outside the component so icons are stable references
// ---------------------------------------------------------------------------
const S = 24;

const ICON: Record<string, React.ReactNode> = {
  straightLine:           <TrendLineIcon          size={S} />,
  horizontalStraightLine: <HorizontalLineIcon     size={S} />,
  verticalStraightLine:   <VerticalLineIcon       size={S} />,
  rayLine:                <RayLineIcon            size={S} />,
  horizontalRayLine:      <HorizontalRayLineIcon  size={S} />,
  verticalRayLine:        <VerticalRayLineIcon    size={S} />,
  segment:                <SegmentLineIcon        size={S} />,
  horizontalSegment:      <HorizontalSegmentIcon  size={S} />,
  verticalSegment:        <VerticalSegmentIcon    size={S} />,
  priceLine:              <PriceLineIcon          size={S} />,
  priceChannelLine:       <PriceChannelIcon       size={S} />,
  parallelStraightLine:   <ParallelLinesIcon      size={S} />,
  fibonacciLine:          <FibonacciIcon          size={S} />,
  simpleAnnotation:       <AnnotationIcon         size={S} />,
  simpleTag:              <TagIcon                size={S} />,
  textBox:                <TextBoxIcon            size={S} />,
  // Custom overlays
  arrow:                          <ArrowIcon          size={S} />,
  rect:                           <RectIcon           size={S} />,
  circle:                         <CircleIcon         size={S} />,
  triangle:                       <TriangleIcon       size={S} />,
  parallelogram:                  <ParallelogramIcon  size={S} />,
  fibonacciExtension:             <FibExtensionIcon   size={S} />,
  fibonacciSpeedResistanceFan:    <FibFanIcon         size={S} />,
  fibonacciCircle:                <FibCircleIcon      size={S} />,
  fibonacciSegment:               <FibSegmentIcon     size={S} />,
  fibonacciSpiral:                <FibSpiralIcon      size={S} />,
  gannBox:                        <GannBoxIcon        size={S} />,
  threeWaves:                     <WaveIcon           size={S} />,
  fiveWaves:                      <WaveIcon           size={S} />,
  eightWaves:                     <WaveIcon           size={S} />,
  anyWaves:                       <WaveIcon           size={S} />,
  abcd:                           <AbcdIcon           size={S} />,
  xabcd:                          <AbcdIcon           size={S} />,
  curve:                          <CurveIcon          size={S} />,
};

const GROUPS: DrawingGroup[] = [
  {
    key: 'lines',
    tools: [
      { mark: 'straightLine',           label: 'Straight Line',   icon: ICON.straightLine           },
      { mark: 'horizontalStraightLine', label: 'Horizontal Line', icon: ICON.horizontalStraightLine },
      { mark: 'verticalStraightLine',   label: 'Vertical Line',   icon: ICON.verticalStraightLine   },
      { mark: 'curve',                  label: 'Curve',           icon: ICON.curve                  },
    ],
  },
  {
    key: 'rays_segs',
    tools: [
      { mark: 'rayLine',           label: 'Ray',               icon: ICON.rayLine           },
      { mark: 'horizontalRayLine', label: 'Horizontal Ray',    icon: ICON.horizontalRayLine },
      { mark: 'verticalRayLine',   label: 'Vertical Ray',      icon: ICON.verticalRayLine   },
      { mark: 'segment',           label: 'Segment',           icon: ICON.segment           },
      { mark: 'horizontalSegment', label: 'Horizontal Segment',icon: ICON.horizontalSegment },
      { mark: 'verticalSegment',   label: 'Vertical Segment',  icon: ICON.verticalSegment   },
    ],
  },
  {
    key: 'price',
    tools: [
      { mark: 'priceLine',            label: 'Price Line',     icon: ICON.priceLine            },
      { mark: 'priceChannelLine',     label: 'Price Channel',  icon: ICON.priceChannelLine     },
      { mark: 'parallelStraightLine', label: 'Parallel Lines', icon: ICON.parallelStraightLine },
    ],
  },
  {
    key: 'fibonacci',
    tools: [
      { mark: 'fibonacciLine',               label: 'Fibonacci Retracement',    icon: ICON.fibonacciLine               },
      { mark: 'fibonacciExtension',          label: 'Fib Extension',            icon: ICON.fibonacciExtension          },
      { mark: 'fibonacciSpeedResistanceFan', label: 'Fib Speed/Resistance Fan', icon: ICON.fibonacciSpeedResistanceFan },
      { mark: 'fibonacciCircle',             label: 'Fib Circle',               icon: ICON.fibonacciCircle             },
      { mark: 'fibonacciSegment',            label: 'Fib Segment',              icon: ICON.fibonacciSegment            },
      { mark: 'fibonacciSpiral',             label: 'Fib Spiral',               icon: ICON.fibonacciSpiral             },
      { mark: 'gannBox',                     label: 'Gann Box',                 icon: ICON.gannBox                     },
    ],
  },
  {
    key: 'annotations',
    tools: [
      { mark: 'simpleAnnotation', label: 'Annotation', icon: ICON.simpleAnnotation },
      { mark: 'simpleTag',        label: 'Tag',        icon: ICON.simpleTag        },
      { mark: 'textBox',          label: 'Text Box',   icon: ICON.textBox          },
    ],
  },
  {
    key: 'shapes',
    tools: [
      { mark: 'rect',          label: 'Rectangle',    icon: ICON.rect          },
      { mark: 'circle',        label: 'Circle',       icon: ICON.circle        },
      { mark: 'triangle',      label: 'Triangle',     icon: ICON.triangle      },
      { mark: 'parallelogram', label: 'Parallelogram',icon: ICON.parallelogram },
      { mark: 'arrow',         label: 'Arrow',        icon: ICON.arrow         },
    ],
  },
  {
    key: 'waves',
    tools: [
      { mark: 'threeWaves', label: '3 Waves',   icon: ICON.threeWaves },
      { mark: 'fiveWaves',  label: '5 Waves',   icon: ICON.fiveWaves  },
      { mark: 'eightWaves', label: '8 Waves',   icon: ICON.eightWaves },
      { mark: 'anyWaves',   label: 'Any Waves', icon: ICON.anyWaves   },
    ],
  },
  {
    key: 'harmonic',
    tools: [
      { mark: 'abcd',  label: 'ABCD',  icon: ICON.abcd  },
      { mark: 'xabcd', label: 'XABCD', icon: ICON.xabcd },
    ],
  },
];

const MAGNET_OPTIONS = [
  { mode: OverlayMode.Normal,       label: 'No Magnet'    },
  { mode: OverlayMode.WeakMagnet,   label: 'Weak Magnet'  },
  { mode: OverlayMode.StrongMagnet, label: 'Strong Magnet'},
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const LABEL_TOOLS = new Set(['simpleAnnotation', 'simpleTag']);
const TEXT_BOX_TOOLS = new Set(['textBox']);

interface LabelPrompt { id: string; text: string; }

interface Props {
  chartRef?: RefObject<Chart | null>;
}

export function DrawingToolbar({ chartRef }: Props) {
  const sidebarRef = useRef<HTMLElement>(null);

  const collapsed    = useChartStore((s) => s.drawingToolbarCollapsed);
  const storeChart   = useChartStore((s) => s.chart);

  // Per-group: which tool was last selected
  const [groupMark, setGroupMark] = useState<Record<string, string>>(
    Object.fromEntries(GROUPS.map(g => [g.key, g.tools[0].mark]))
  );

  // The mark currently in drawing mode (highlighted)
  const [activeMark, setActiveMark] = useState<string | null>(null);

  // Flyout panel state
  const [flyout, setFlyout] = useState<FlyoutState | null>(null);

  // Overlay settings
  const [mode,    setMode]    = useState<OverlayMode>(OverlayMode.Normal);
  const [locked,  setLocked]  = useState(false);
  const [showAll, setShowAll] = useState(true);

  // Label prompt for annotation/tag
  const [labelPrompt, setLabelPrompt] = useState<LabelPrompt | null>(null);

  // Overlay style editor
  const [editingOverlayId, setEditingOverlayId] = useState<string | null>(null);

  // Text box editor
  const [textBoxEditId, setTextBoxEditId] = useState<string | null>(null);

  const setDrawingTaskActive = useChartStore((s) => s.setDrawingTaskActive);

  // Mirror "is the user mid-task here" into the shared store so other UI
  // (e.g. the nudge scheduler) can avoid popping up over an active drawing.
  useEffect(() => {
    const busy = activeMark !== null || labelPrompt !== null || editingOverlayId !== null || textBoxEditId !== null;
    setDrawingTaskActive(busy);
  }, [activeMark, labelPrompt, editingOverlayId, textBoxEditId, setDrawingTaskActive]);

  // Clear the busy flag on unmount so it can't get stuck true if the toolbar
  // itself unmounts mid-task (e.g. navigating away).
  useEffect(() => {
    return () => setDrawingTaskActive(false);
  }, [setDrawingTaskActive]);

  const handleOverlayDoubleClick = useCallback((event: OverlayEvent): boolean => {
    if (event.overlay.name === 'textBox') {
      setTextBoxEditId(event.overlay.id);
    } else {
      setEditingOverlayId(event.overlay.id);
    }
    return false;
  }, []);

  // Register double-click handler on all existing overlays whenever the chart
  // changes — and when drawings are added outside the toolbar (restored from a
  // shared link), which never went through activateTool().
  const drawingOverlays = useChartStore((s) => s.drawingOverlays);
  useEffect(() => {
    storeChart?.overrideOverlay({ onDoubleClick: handleOverlayDoubleClick } as Partial<OverlayCreate>);
  }, [storeChart, handleOverlayDoubleClick, drawingOverlays]);

  // Close flyout on click-outside (both toolbar and flyout portal)
  useEffect(() => {
    function handler(e: PointerEvent) {
      const target = e.target as HTMLElement;
      if (sidebarRef.current?.contains(target)) return;
      if (target.closest('[data-drawing-flyout]')) return;
      setFlyout(null);
    }
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, []);

  function getChart(): Chart | null {
    return chartRef?.current ?? storeChart;
  }

  function openFlyout(e: React.MouseEvent, type: FlyoutState['type'], key: string) {
    // Toggle: clicking the same flyout trigger closes it
    if (flyout?.type === type && flyout.key === key) {
      setFlyout(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Clamp x so flyout (min 180px wide) never overflows the viewport right edge
    const x = Math.min(rect.right + 4, window.innerWidth - 184);
    setFlyout({ type, key, x, y: rect.top });
  }

  function activateTool(mark: string) {
    setFlyout(null);
    const chart = getChart();
    if (!chart) return;
    if (activeMark === mark) {
      setActiveMark(null);
      return;
    }
    const overlayCreate: OverlayCreate = {
      name: mark, mode, lock: locked, visible: showAll, onDoubleClick: handleOverlayDoubleClick,
      // Clears the "armed" state for every tool once placement finishes —
      // not just label/text-box — so drawingTaskActive (and thus nudge
      // queueing) reflects "still placing" rather than "ever selected".
      onDrawEnd: (event) => {
        const id = event.overlay.id;
        useChartStore.getState().addDrawingOverlay(id, useChartStore.getState().symbol);
        setActiveMark(null);
        if (LABEL_TOOLS.has(mark)) {
          setLabelPrompt({ id, text: '' });
        } else if (TEXT_BOX_TOOLS.has(mark)) {
          setTextBoxEditId(id);
        }
        return false;
      },
    };
    if (TEXT_BOX_TOOLS.has(mark)) {
      overlayCreate.extendData = { ...TEXT_BOX_DEFAULT };
    }
    chart.createOverlay(overlayCreate);
    setActiveMark(mark);
    trackDrawingTool(mark);
  }

  function selectGroupTool(groupKey: string, tool: DrawingTool) {
    setGroupMark(prev => ({ ...prev, [groupKey]: tool.mark }));
    activateTool(tool.mark);
  }

  function selectMagnetMode(m: OverlayMode) {
    setMode(m);
    setFlyout(null);
  }

  function toggleLock() {
    const next = !locked;
    setLocked(next);
    getChart()?.overrideOverlay({ lock: next } as Partial<OverlayCreate>);
  }

  function toggleVisible() {
    const next = !showAll;
    setShowAll(next);
    getChart()?.overrideOverlay({ visible: next } as Partial<OverlayCreate>);
  }

  function removeAll() {
    getChart()?.removeOverlay();
    useChartStore.getState().clearDrawingOverlays();
    setActiveMark(null);
    setFlyout(null);
  }

  function confirmLabel() {
    if (!labelPrompt) return;
    if (labelPrompt.text.trim()) {
      getChart()?.overrideOverlay({ id: labelPrompt.id, extendData: labelPrompt.text.trim() } as Partial<OverlayCreate>);
    }
    setLabelPrompt(null);
  }

  const magnetOn = mode !== OverlayMode.Normal;
  const magnetBadge = mode === OverlayMode.WeakMagnet ? 'W' : mode === OverlayMode.StrongMagnet ? 'S' : null;

  if (collapsed) return null;

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* Expanded — full sidebar                                          */}
      {/* ---------------------------------------------------------------- */}
      <aside
        ref={sidebarRef}
        className="flex flex-col items-center w-14 py-2 pl-[5px] glass shrink-0" style={{ borderRadius: 0, borderTop: 'none', borderBottom: 'none', borderLeft: 'none' }}
      >
          {/* Drawing groups */}
          {GROUPS.map(group => {
            const mark        = groupMark[group.key];
            const hasFlyout   = group.tools.length > 1;
            const groupActive = group.tools.some(t => t.mark === activeMark);
            const flyoutOpen  = flyout?.type === 'group' && flyout.key === group.key;

            return (
              <div
                key={group.key}
                className={`flex items-center w-full h-11 mb-1 rounded ${groupActive ? 'bg-accent/10' : ''}`}
              >
                {/* Main icon — activates last-used tool */}
                <button
                  onClick={() => activateTool(mark)}
                  title={group.tools.find(t => t.mark === mark)?.label}
                  className={`flex-1 flex items-center justify-center h-full rounded-l transition-colors ${
                    activeMark === mark
                      ? 'text-accent'
                      : 'text-muted hover:text-text hover:bg-panel-hover'
                  }`}
                >
                  {ICON[mark]}
                </button>

                {/* Chevron — opens flyout */}
                {hasFlyout && (
                  <button
                    onClick={e => openFlyout(e, 'group', group.key)}
                    className={`w-4 flex items-center justify-center h-full rounded-r transition-colors ${
                      flyoutOpen
                        ? 'text-accent/80 bg-accent/10'
                        : 'text-muted/40 hover:text-muted hover:bg-panel-hover'
                    }`}
                    aria-label="More tools"
                  >
                    <ChevronRightIcon size={8} />
                  </button>
                )}
              </div>
            );
          })}

          {/* Divider */}
          <div className="w-8 h-px bg-border/50 my-2 shrink-0" />

          {/* Magnet — opens mode flyout */}
          <button
            onClick={e => openFlyout(e, 'magnet', 'magnet')}
            title={MAGNET_OPTIONS.find(o => o.mode === mode)?.label}
            className={`relative flex flex-col items-center justify-center w-9 h-9 rounded transition-colors mb-px ${
              magnetOn
                ? 'text-accent bg-accent/20'
                : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            <MagnetIcon size={16} />
            {magnetBadge && (
              <span className="text-[8px] font-bold leading-none mt-px">{magnetBadge}</span>
            )}
          </button>

          {/* Lock toggle */}
          <button
            onClick={toggleLock}
            title={locked ? 'Unlock All Drawings' : 'Lock All Drawings'}
            className={`flex items-center justify-center w-9 h-9 rounded transition-colors mb-px ${
              locked
                ? 'text-accent bg-accent/20'
                : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            {locked ? <LockIcon size={18} /> : <UnlockIcon size={18} />}
          </button>

          {/* Visibility toggle */}
          <button
            onClick={toggleVisible}
            title={showAll ? 'Hide All Drawings' : 'Show All Drawings'}
            className={`flex items-center justify-center w-9 h-9 rounded transition-colors mb-px ${
              !showAll
                ? 'text-accent bg-accent/20'
                : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            {showAll ? <EyeIcon size={18} /> : <EyeOffIcon size={18} />}
          </button>

          {/* Push trash to bottom */}
          <div className="flex-1" />

          {/* Remove all */}
          <button
            onClick={removeAll}
            title="Remove All Drawings"
            className="flex items-center justify-center w-9 h-9 rounded text-muted hover:text-red-400 hover:bg-panel-hover transition-colors mb-1"
          >
            <TrashIcon size={18} />
          </button>
        </aside>

      {/* ----------------------------------------------------------------- */}
      {/* Flyout portal                                                      */}
      {/* ----------------------------------------------------------------- */}
      {flyout && createPortal(
        <div
          data-drawing-flyout="1"
          className="fixed glass-modal rounded-xl py-1.5 animate-menu-enter"
          style={{ left: flyout.x, top: flyout.y, zIndex: Z_INDEX.floatingPopover, minWidth: 180 }}
        >
          {flyout.type === 'group' && (() => {
            const group = GROUPS.find(g => g.key === flyout.key);
            return group?.tools.map(tool => (
              <button
                key={tool.mark}
                onClick={() => selectGroupTool(flyout.key, tool)}
                className={`flex items-center gap-3 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-modal-hover ${
                  activeMark === tool.mark ? 'text-accent' : 'text-modal-text'
                }`}
              >
                  <span className={activeMark === tool.mark ? 'text-accent' : 'text-modal-muted'}>
                  {tool.icon}
                </span>
                {tool.label}
                {activeMark === tool.mark && (
                  <span className="ml-auto text-accent text-xs">●</span>
                )}
              </button>
            ));
          })()}

          {flyout.type === 'magnet' && MAGNET_OPTIONS.map(opt => (
            <button
              key={opt.mode}
              onClick={() => selectMagnetMode(opt.mode)}
              className={`flex items-center gap-3 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-modal-hover ${
                mode === opt.mode ? 'text-accent' : 'text-modal-text'
              }`}
            >
              <span className={mode === opt.mode ? 'text-accent' : 'text-modal-muted'}>
                <MagnetIcon size={16} />
              </span>
              {opt.label}
              {mode === opt.mode && (
                <span className="ml-auto text-accent text-xs">●</span>
              )}
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Label prompt dialog for annotation / tag                          */}
      {/* ----------------------------------------------------------------- */}
      {labelPrompt && createPortal(
        <div
          className="fixed inset-0 z-topmost flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
          onPointerDown={(e) => { if (e.target === e.currentTarget) setLabelPrompt(null); }}
        >
          <div className="bg-modal-bg border border-border rounded-xl shadow-2xl p-5 w-72 animate-modal-enter">
            <p className="text-base text-modal-muted mb-3">Label <span className="text-modal-text/50">(optional — press Enter or OK)</span></p>
            <input
              autoFocus
              type="text"
              value={labelPrompt.text}
              onChange={e => setLabelPrompt(p => p ? { ...p, text: e.target.value } : null)}
              onKeyDown={e => {
                if (e.key === 'Enter') confirmLabel();
                if (e.key === 'Escape') setLabelPrompt(null);
              }}
              className="w-full bg-bg border border-border rounded-lg px-3 py-2.5 text-base text-modal-text focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              placeholder="Type a label…"
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => setLabelPrompt(null)}
                className="px-3 py-1.5 text-sm text-modal-muted hover:text-modal-text rounded-lg hover:bg-modal-hover transition-colors"
              >
                Skip
              </button>
              <button
                onClick={confirmLabel}
                className="px-3 py-1.5 text-sm bg-accent/20 text-accent hover:bg-accent/30 rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Overlay style editor modal                                        */}
      {/* ----------------------------------------------------------------- */}
      {editingOverlayId && (
        <OverlayStyleModal
          overlayId={editingOverlayId}
          onClose={() => setEditingOverlayId(null)}
        />
      )}

      {textBoxEditId && (
        <TextBoxModal
          overlayId={textBoxEditId}
          onClose={() => setTextBoxEditId(null)}
        />
      )}
    </>
  );
}
